import { create } from 'zustand';
import type { RoutePlanLngLat } from '../utils/fetch';

export type PointSourceMode = 'pick' | 'input';

/** 路径规划端点：起点 / 终点 */
export type RouteEndpoint = 'start' | 'end';

export type RouteStage =
  | 'idle'
  | 'pickingStart'
  | 'pickingEnd'
  | 'readyToPlan'
  | 'loading'
  | 'success'
  | 'fail';

/**
 * 路径规划状态（只读快照字段，供 IRoutePlanStoreBridge.getSnapshot 返回）
 */
export interface RoutePlanState {
  planningMode: boolean;
  pointSourceMode: PointSourceMode;
  routeStage: RouteStage;
  startPoint: RoutePlanLngLat | null;
  endPoint: RoutePlanLngLat | null;
  /** 输入框原始文本（input 模式可编辑，pick 模式由选点回填） */
  startInput: string;
  endInput: string;
  /** 非法坐标文案（红框 + 状态区展示） */
  startInputError: string | null;
  endInputError: string | null;
  /** 里程，单位：米 */
  distance: number | null;
  /** 耗时，单位：秒 */
  duration: number | null;
  /** 规划失败文案 */
  planError: string | null;
}

/**
 * 路径规划独立 store（按领域拆分，避免与编辑器交互状态互相通知，见 AGENTS.md 坑12）
 *
 * 职责：路径规划模式 / 选点方式 / 流程阶段 / 起终点 / 输入框 / 规划结果
 * 不包含：地图渲染（RoutePlanManager）、浮层展开收起（面板组件内局部 state）
 */
interface RoutePlanStore extends RoutePlanState {
  enterPlanMode: () => void;
  exitPlanMode: () => void;
  setPointSourceMode: (mode: PointSourceMode) => void;
  setRouteStage: (stage: RouteStage) => void;
  setStartPoint: (point: RoutePlanLngLat | null) => void;
  setEndPoint: (point: RoutePlanLngLat | null) => void;
  setStartInput: (text: string) => void;
  setEndInput: (text: string) => void;
  setInputError: (which: RouteEndpoint, msg: string | null) => void;
  setRouteResult: (distance: number, duration: number) => void;
  clearRouteResult: () => void;
  setPlanError: (msg: string | null) => void;
  /** 清点清线清里程：pick 模式回 pickingStart，input 模式清空输入框回 idle */
  resetPoints: () => void;
}

const initialRoutePlanState: RoutePlanState = {
  planningMode: false,
  pointSourceMode: 'pick',
  routeStage: 'idle',
  startPoint: null,
  endPoint: null,
  startInput: '',
  endInput: '',
  startInputError: null,
  endInputError: null,
  distance: null,
  duration: null,
  planError: null,
};

export const useRoutePlanStore = create<RoutePlanStore>((set) => ({
  ...initialRoutePlanState,

  enterPlanMode: () =>
    set({
      ...initialRoutePlanState,
      planningMode: true,
      pointSourceMode: 'pick',
      routeStage: 'pickingStart',
    }),

  exitPlanMode: () => set({ ...initialRoutePlanState }),

  setPointSourceMode: (mode) => set({ pointSourceMode: mode }),
  setRouteStage: (stage) => set({ routeStage: stage }),
  setStartPoint: (point) => set({ startPoint: point }),
  setEndPoint: (point) => set({ endPoint: point }),
  setStartInput: (text) => set({ startInput: text }),
  setEndInput: (text) => set({ endInput: text }),

  setInputError: (which, msg) =>
    set(
      which === 'start'
        ? { startInputError: msg }
        : { endInputError: msg }
    ),

  setRouteResult: (distance, duration) => set({ distance, duration }),
  clearRouteResult: () => set({ distance: null, duration: null }),
  setPlanError: (msg) => set({ planError: msg }),

  resetPoints: () =>
    set((state) => ({
      startPoint: null,
      endPoint: null,
      startInput: '',
      endInput: '',
      startInputError: null,
      endInputError: null,
      distance: null,
      duration: null,
      planError: null,
      routeStage: state.pointSourceMode === 'pick' ? 'pickingStart' : 'idle',
    })),
}));

/**
 * 共享谓词：是否处于"地图选点阶段"（规划模式 + pick 来源 + pickingStart/pickingEnd）。
 * 统一消费方：RoutePlanManager（click 订阅时机）、index.tsx（选点光标）、
 * MapEditorCoordinator.handleSelect（picking 阶段屏蔽面详情选中）。
 */
export const selectIsRoutePicking = (state: RoutePlanState): boolean =>
  state.planningMode &&
  state.pointSourceMode === 'pick' &&
  (state.routeStage === 'pickingStart' || state.routeStage === 'pickingEnd');
